import { getLayerSlug } from './routeUtils';
import type { GisFeature, ServiceLayer } from '../types/gis';

/**
 * Generates a unique, URL-safe key for a GIS feature to be used in sharing.
 */
export const getFeatureShareKey = (feature: GisFeature, layer: ServiceLayer): string | null => {
  const p = feature.properties;
  if (!p) return null;

  switch (layer) {
    case 'PINCODE':
    case 'PDS':
    case 'POLICE':
    case 'HEALTH':
    case 'TNEB':
    case 'CONSTITUENCY':
    case 'LOCAL_BODIES_V2':
      return (p.pincode || p.PIN_CODE || p.pin_code || p.Pincode || '').toString() || null;
    
    case 'VETERINARY': {
      const village = (p.village_na || '').toString().trim();
      const taluk = (p.taluk_name || p.taluk || '').toString().trim();
      if (!village) return null;
      // Create a unique compound key: vet_village_taluk
      const safeStr = `${village}_${taluk}`.replace(/\s+/g, '_').toLowerCase();
      return `vet_${safeStr}`;
    }

    case 'REGISTRATION': {
      const name = (p.sro_name || p.office_nam || '').toString().trim();
      if (!name) return null;
      const safeStr = name.replace(/\s+/g, '_').toLowerCase();
      return `sro_${safeStr}`;
    }

    case 'LAND_PARCEL': {
      // Use coordinates as the key for deep linking
      const centroid = (p.centroid as string)?.toString();
      if (!centroid || typeof centroid !== 'string') return null;
      const [lat, lng] = centroid.split(',').map(Number);
      if (isNaN(lat) || !lng) return null;
      return `${lat.toFixed(6)},${lng.toFixed(6)}`;
    }

    case 'TEMPLES': {
      const props = (feature.properties || feature) as unknown as { id?: string; dc?: string; g?: { dc?: string } };
      const id = props.id;
      const dc = props.g?.dc || props.dc;
      if (!id || !dc) return null;
      return `temple_${dc}_${id}`;
    }
    
    default:
      return null;
  }
};

/**
 * Generates a full shareable URL from a raw key string.
 */
export const getRawShareUrl = (key: string | null, layer: ServiceLayer): string | null => {
  if (!key) return null;

  const baseUrl = window.location.origin + window.location.pathname;
  const url = new URL(baseUrl);
  
  // Generate a clean dynamic path like domain.com/pincode/625006 instead of query params
  url.pathname = `/${getLayerSlug(layer)}/${key}`;
  
  return url.toString();
};

/**
 * Generates a full shareable URL for a feature.
 */
export const getFeatureShareUrl = (feature: GisFeature, layer: ServiceLayer): string | null => {
  const key = getFeatureShareKey(feature, layer);
  return getRawShareUrl(key, layer);
};
